import type { ReactNode } from 'react';

type AlertVariant = 'error' | 'warning' | 'info';

interface AlertProps {
  variant?: AlertVariant;
  title?: string;
  children: ReactNode;
}

// `warning` MUST NOT reuse `error`'s `border-accent` — that made
// degradation/sandbox notices (quote.partial, onboarding sandbox banner,
// "provedor precisa de reconexão") visually indistinguishable from a real
// product error (ux-ui-reviewer finding, TTP-287). `border-primary` +
// `bg-primary-wash` is the only other pre-existing semantic pair in the
// theme (`--color-primary`/`--color-primary-wash` in index.css) — reused
// here rather than inventing a new token outside the design system.
const VARIANT_CLASSES: Record<AlertVariant, string> = {
  error: 'border-accent bg-accent/5',
  warning: 'border-primary bg-primary-wash',
  info: 'border-border bg-muted/40',
};

// role="alert" (assertive live region) only for the genuinely urgent
// variant — role="status" (polite) for warning/info, so screen readers
// aren't interrupted for non-blocking copy (WCAG 2.2 AA — "erros
// anunciados" applies to errors, not every degradation/sandbox notice).
const VARIANT_ROLE: Record<AlertVariant, 'alert' | 'status'> = {
  error: 'alert',
  warning: 'status',
  info: 'status',
};

/**
 * Renders an RFC 9457 problem (`title`/`detail`) or any other inline
 * banner — used for API errors without a `field` match, partial-quote
 * warnings, sandbox notices, etc.
 */
export function Alert({ variant = 'info', title, children }: AlertProps) {
  return (
    <div role={VARIANT_ROLE[variant]} className={`rounded-none border px-4 py-3 text-sm leading-relaxed ${VARIANT_CLASSES[variant]}`}>
      {title && <p className="mb-1 font-medium text-foreground">{title}</p>}
      <div className="text-muted-foreground">{children}</div>
    </div>
  );
}
